/**
 * Eigene URLs für Unteransichten des Dashboards (NT-LAYOUT-01).
 *
 * - Vier Crime-Ops-Sektionen haben eigene Pfade, damit die Bottom-Nav (Mobile) direkt dorthin
 *   springen und ihren aktiven Button aus der URL ableiten kann. Gerendert wird weiterhin der
 *   Crime-Ops-Tab mit vorausgewählter Sektion — keine eigene View.
 * - Pfandflaschen-Imperium und Werkstatt (Jobs-Unteransichten) haben eigene Pfade, damit
 *   Browser-Zurück innerhalb des Dashboards bleibt.
 *
 * Reine Daten + Lookups, kein DOM — von dashboard.ts, crimeOps.ts und BottomNav.ts geteilt.
 */

/** Crime-Ops-Sektion → eigener Pfad. Sektions-IDs wie in crimeOps.ts (`Section`). */
export const CRIME_OPS_SECTION_PATHS = {
  labor:        '/labor',
  'gewächshaus': '/gewaechshaus',
  rolltisch:    '/rolltisch',
  viertel:      '/viertel',
} as const;

export type RoutedCrimeOpsSection = keyof typeof CRIME_OPS_SECTION_PATHS;

export const CRIME_OPS_BASE_PATH = '/business/crime-ops';

/** Liefert die Crime-Ops-Sektion für einen der eigenen Sektions-Pfade, sonst `null`. */
export function getCrimeOpsSectionForPath(path: string): RoutedCrimeOpsSection | null {
  for (const [section, sectionPath] of Object.entries(CRIME_OPS_SECTION_PATHS)) {
    if (sectionPath === path) return section as RoutedCrimeOpsSection;
  }
  return null;
}

/** Pfad für eine Crime-Ops-Sektion: eigener Pfad, falls vorhanden, sonst der Crime-Ops-Basispfad. */
export function getPathForCrimeOpsSection(section: string): string {
  return (CRIME_OPS_SECTION_PATHS as Record<string, string>)[section] ?? CRIME_OPS_BASE_PATH;
}

/** true für den Crime-Ops-Basispfad und alle eigenen Sektions-Pfade. */
export function isCrimeOpsPath(path: string): boolean {
  return path === CRIME_OPS_BASE_PATH || getCrimeOpsSectionForPath(path) !== null;
}

/** Jobs-Unteransichten mit eigenem Pfad. */
export type JobsView = 'list' | 'pfandBetrieb' | 'werkstatt' | 'werkstattCraft';

export const JOBS_VIEW_PATHS: Readonly<Record<JobsView, string>> = {
  list:           '/jobs',
  pfandBetrieb:   '/jobs/pfand-betrieb',
  werkstatt:      '/jobs/werkstatt',
  werkstattCraft: '/jobs/werkstatt/craft',
};

/** Liefert die Jobs-Unteransicht für einen Pfad, sonst `null` (kein Jobs-Pfad). */
export function getJobsViewForPath(path: string): JobsView | null {
  if (path === '/dashboard') return 'list';
  for (const [view, viewPath] of Object.entries(JOBS_VIEW_PATHS)) {
    if (viewPath === path) return view as JobsView;
  }
  return null;
}
